/** tm/legal/LegalNewsDetail.tsx — 뉴스 묶음 상세 (GET /news/:id): 같은 소식 기사 목록(새 창) · 별표 */
import { useCallback, useEffect, useState } from 'react';
import { C, T } from '../components/ui';
import { getNews, setNewsStar, sendNewsToGov } from './legalApi';
import { btn, shortTime, safeHttp, errMsg, AMBER, MUTED } from './legalTheme';
import { DetailSkeleton, DetailHead, MetaGrid, DetailEmpty, DETAIL_MAX, actsRow } from './LegalDetailParts';
import type { NewsDetail } from './legalTypes';

interface Props {
  id: number; mobile: boolean;
  onBack: () => void; onToast: (m: string, k?: 'ok' | 'error') => void; onPatch: (p: { starred?: boolean }) => void;
}

export default function LegalNewsDetail({ id, mobile, onBack, onToast, onPatch }: Props) {
  const [n, setN] = useState<NewsDetail | null>(null);
  const [err, setErr] = useState('');
  const [sending, setSending] = useState(false);
  const load = useCallback(async () => {
    setN(null); setErr('');
    try { setN(await getNews(id)); } catch (e) { setErr(errMsg(e, '기사를 불러오지 못했습니다')); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (err) return <>{mobile && <button type="button" className="lw-f" onClick={onBack} style={btn()}>← 목록</button>}<DetailEmpty error={err} onRetry={load} /></>;
  if (!n) return <DetailSkeleton />;

  const arts = [...(n.articles || [])].sort((a, b) => (a.published_at < b.published_at ? 1 : -1));
  const first = arts.length ? arts[arts.length - 1] : null;
  const star = async () => {
    const next = !n.starred;
    setN({ ...n, starred: next }); onPatch({ starred: next });
    try { await setNewsStar(n.id, next); onToast(next ? '별표했습니다' : '별표를 해제했습니다'); }
    catch (e) { setN({ ...n, starred: !next }); onPatch({ starred: !next }); onToast(errMsg(e, '별표하지 못했습니다'), 'error'); }
  };

  // 금융위 · 신보 · 기보 글(게시판) — 모집 · 신청 공고면 「공고 검토함으로」 (2026-10-08)
  const board = arts.find(a => a.provider === 'board') || null;
  const toGov = async () => {
    if (!board || sending) return;
    setSending(true);
    try {
      const r = await sendNewsToGov(board.url);
      onToast(r.data?.existed ? '이미 공고 검토함에 있는 글입니다 — ★ 관심으로 표시했습니다' : '공고 검토함에 넣었습니다 — ★ 관심 · AI 분석 대기');
    } catch (e) { onToast(errMsg(e, '공고 검토함에 넣지 못했습니다'), 'error'); }
    finally { setSending(false); }
  };

  return (
    <div>
      <DetailHead group="뉴스" label="뉴스" topics={n.topics || []} title={n.title} mobile={mobile} onBack={onBack} />
      <MetaGrid mobile={mobile} rows={[
        ['첫 보도', `${first?.source || n.lead_source}${first ? ` · ${shortTime(first.published_at)}` : ''}`],
        ['보도 수', `${n.article_count || arts.length}곳`],
        ['최근 보도', shortTime(n.last_published_at)],
      ]} />
      <div style={{ borderTop: `1px solid ${C.border}`, maxWidth: DETAIL_MAX, marginBottom: 12 }}>
        {arts.map((a, i) => {
          const u = safeHttp(a.url);
          const inner = <>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: T.md, lineHeight: 1.5, color: u ? C.text : C.sub }}>{a.title}</span>
              {a.snippet && <span className="lw-clamp" style={{ fontSize: T.sm, color: MUTED, marginTop: 2, lineHeight: 1.5 }}>{a.snippet}</span>}
            </span>
            <small style={{ flexShrink: 0, textAlign: 'right', color: MUTED, fontSize: T.sm, lineHeight: 1.5, whiteSpace: 'nowrap' }}>
              {a.source}<br /><span style={{ fontVariantNumeric: 'tabular-nums' }}>{shortTime(a.published_at)}</span>
            </small>
          </>;
          const st = { display: 'flex', gap: 12, padding: '9px 4px', borderBottom: `1px solid ${C.border}`, textDecoration: 'none' } as const;
          return u
            ? <a key={`${i}${a.url}`} className="lw-f lw-row" href={u} target="_blank" rel="noopener noreferrer" style={st}>{inner}</a>
            : <div key={`${i}${a.url}`} style={st}>{inner}</div>;
        })}
      </div>
      <p style={{ fontSize: T.sm, color: MUTED, maxWidth: DETAIL_MAX, lineHeight: 1.6, margin: '0 0 12px' }}>
        같은 소식을 다룬 기사를 한 묶음으로 보여줍니다. 제목을 누르면 기사 원문이 새 창에서 열립니다. 뉴스는 새 소식 알림을 보내지 않습니다.
      </p>
      <div style={actsRow}>
        <button type="button" className="lw-f" onClick={star} aria-pressed={n.starred} style={{ ...btn(), color: n.starred ? AMBER : C.sub }}>{n.starred ? '★ 별표 해제' : '☆ 별표'}</button>
        {board && (
          <button type="button" className="lw-f" data-action="news-to-gov" onClick={toGov} disabled={sending} title={`${board.source} 글을 정부지원사업 공고 검토함에 넣고 AI 분석을 맡깁니다`}
            style={{ ...btn(), color: C.accent, opacity: sending ? 0.6 : 1 }}>{sending ? '넣는 중…' : '공고 검토함으로'}</button>
        )}
      </div>
    </div>
  );
}
